import { format, isToday, isYesterday, parseISO } from "date-fns";
import { useAuthStore } from "../../stores/auth-store";
import { useActivityLogs } from "../../hooks/useActivityLogs";
import { GradientSpinner } from "../common/GradientSpinner";
import { AiSparkleIcon } from "../ui/AiSparkleIcon";
import { Icon } from "../ui/Icon";
import SettingsLinear from "~icons/solar/settings-linear";
import { cn } from "../../lib/utils";
import { env } from "../../lib/env";
import { getInitials } from "./candidate-utils";
import { CandidateAvatarImage } from "./CandidateAvatarImage";
import type { ActivityLogItem, ActivityActorType } from "../../types/activity";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getDateGroupLabel(dateStr: string): string {
  const date = parseISO(dateStr);
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "EEEE, MMM d, yyyy").toUpperCase();
}

function getDateKey(isoString: string): string {
  // Use only the date portion for grouping
  return isoString.slice(0, 10);
}

function formatEntryTime(isoString: string): string {
  return format(parseISO(isoString), "HH:mm");
}

interface DateGroup {
  label: string;
  dateKey: string;
  items: ActivityLogItem[];
}

function groupByDate(items: ActivityLogItem[]): DateGroup[] {
  const map = new Map<string, DateGroup>();

  for (const item of items) {
    const dateKey = getDateKey(item.occurred_at);
    if (!map.has(dateKey)) {
      map.set(dateKey, {
        label: getDateGroupLabel(item.occurred_at),
        dateKey,
        items: [],
      });
    }
    map.get(dateKey)!.items.push(item);
  }

  return Array.from(map.values());
}

// ---------------------------------------------------------------------------
// Actor Avatar
// ---------------------------------------------------------------------------

interface ActorAvatarProps {
  actorType?: ActivityActorType;
  actorName?: string;
  avatarUrl?: string;
}

function ActorAvatar({ actorType, actorName, avatarUrl }: ActorAvatarProps) {
  if (actorType === "ai") {
    return (
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/5">
        <AiSparkleIcon className="size-5" />
      </div>
    );
  }

  if (actorType === "system") {
    return (
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full border border-black-50 bg-bg-page">
        <Icon icon={SettingsLinear} size="sm" className="text-grey" />
      </div>
    );
  }

  // user or candidate
  return (
    <CandidateAvatarImage
      src={avatarUrl}
      alt={actorName ?? ""}
      className="size-9 shrink-0 rounded-full object-cover"
      fallback={
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-gradient-start to-gradient-end">
          <span className="text-body-xs font-semibold text-white">
            {getInitials(actorName)}
          </span>
        </div>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Description renderer
// ---------------------------------------------------------------------------

interface DescriptionProps {
  item: ActivityLogItem;
  resolvedActorName: string | undefined;
}

function ActivityDescription({ item, resolvedActorName }: DescriptionProps) {
  const { description, action, metadata, actor_name } = item;

  // Score highlight for confidence_score_initial
  const score =
    action === "confidence_score_initial" &&
    metadata?.score !== undefined &&
    metadata.score !== null
      ? String(metadata.score)
      : null;

  // Build segments by splitting on actor name first, then on score
  const segments = buildSegments(
    description,
    resolvedActorName ?? actor_name,
    score
  );

  return (
    <p className="text-body-sm text-grey">
      {segments.map((seg, i) => {
        if (seg.type === "actor") {
          return (
            <span key={i} className="font-semibold text-primary">
              {seg.text}
            </span>
          );
        }
        if (seg.type === "score") {
          return (
            <span key={i} className="font-bold text-[#0fa144]">
              {seg.text}
            </span>
          );
        }
        return <span key={i}>{seg.text}</span>;
      })}
    </p>
  );
}

type Segment = { type: "text" | "actor" | "score"; text: string };

function splitOnTerm(
  text: string,
  term: string,
  segType: "actor" | "score"
): Segment[] {
  if (!term) return [{ type: "text", text }];
  const parts = text.split(term);
  const result: Segment[] = [];
  for (let i = 0; i < parts.length; i++) {
    if (parts[i]) result.push({ type: "text", text: parts[i] });
    if (i < parts.length - 1) result.push({ type: segType, text: term });
  }
  return result;
}

function buildSegments(
  description: string,
  actorName: string | null | undefined,
  score: string | null
): Segment[] {
  // Start with the full description as one text segment
  let segments: Segment[] = [{ type: "text", text: description }];

  // Split on actor name
  if (actorName) {
    segments = segments.flatMap((seg) => {
      if (seg.type !== "text") return [seg];
      return splitOnTerm(seg.text, actorName, "actor");
    });
  }

  // Split on score
  if (score) {
    segments = segments.flatMap((seg) => {
      if (seg.type !== "text") return [seg];
      return splitOnTerm(seg.text, score, "score");
    });
  }

  return segments;
}

// ---------------------------------------------------------------------------
// Single Activity Entry
// ---------------------------------------------------------------------------

interface ActivityEntryProps {
  item: ActivityLogItem;
  isLast: boolean;
  currentUserId: string | undefined;
}

function ActivityEntry({ item, isLast, currentUserId }: ActivityEntryProps) {
  const isCurrentUser =
    !!item.actor_id && !!currentUserId && item.actor_id === currentUserId;

  const resolvedActorName = isCurrentUser ? "You" : item.actor_name;

  return (
    <div className="flex gap-3">
      {/* Left column: avatar + vertical connector */}
      <div className="flex flex-col items-center">
        <ActorAvatar
          actorType={item.actor_type}
          actorName={resolvedActorName}
          avatarUrl={item.actor_avatar_url}
        />
        {!isLast && (
          <div className="mt-2 w-px flex-1 border-l border-dashed border-black-50" />
        )}
      </div>

      {/* Right column: content */}
      <div className={cn("min-w-0 flex-1", !isLast && "pb-5")}>
        {/* Title row */}
        <div className="flex items-start justify-between gap-2">
          <p className="text-body-md font-semibold text-secondary leading-snug">
            {item.title}
          </p>
          <time
            dateTime={item.occurred_at}
            className="shrink-0 text-body-xs text-grey"
          >
            {formatEntryTime(item.occurred_at)}
          </time>
        </div>

        {/* Description */}
        {item.description && (
          <div className="mt-0.5">
            <ActivityDescription
              item={item}
              resolvedActorName={resolvedActorName}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Date Group Card
// ---------------------------------------------------------------------------

interface DateGroupCardProps {
  group: DateGroup;
  currentUserId: string | undefined;
}

function DateGroupCard({ group, currentUserId }: DateGroupCardProps) {
  return (
    <div className="rounded-[20px] border border-black-50 bg-white">
      {/* Date header */}
      <div className="border-b border-black-50 px-5 py-3">
        <p className="text-body-xs font-semibold uppercase tracking-wider text-grey">
          {group.label}
        </p>
      </div>

      {/* Entries */}
      <div className="px-5 pt-4 pb-3">
        {group.items.map((item, idx) => (
          <ActivityEntry
            key={item.id}
            item={item}
            isLast={idx === group.items.length - 1}
            currentUserId={currentUserId}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export interface ActivitiesLogProps {
  candidateStatusId: string | undefined;
}

export function ActivitiesLog({ candidateStatusId }: ActivitiesLogProps) {
  const currentUser = useAuthStore((s) => s.user);
  const {
    items,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useActivityLogs(candidateStatusId);

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <GradientSpinner size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center gap-3">
        <p className="text-body-md text-error">{error}</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded-full border border-black-50 px-4 py-1.5 text-body-sm font-medium text-grey hover:bg-bg-page"
        >
          Retry
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-body-md text-grey">No activities yet.</p>
      </div>
    );
  }

  // Confidence-score activity rows are held behind the same flag as the
  // score UI — filtered out entirely (not just unhighlighted) when off.
  const visibleItems = env.FEATURE_CONFIDENCE_SCORE_UI
    ? items
    : items.filter((i) => i.action !== "confidence_score_initial");

  if (visibleItems.length === 0) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-body-md text-grey">No activities yet.</p>
      </div>
    );
  }

  const groups = groupByDate(visibleItems);

  return (
    <div className="flex flex-col gap-4 p-4">
      {groups.map((group) => (
        <DateGroupCard
          key={group.dateKey}
          group={group}
          currentUserId={currentUser?.id}
        />
      ))}

      {/* Load more */}
      {hasNextPage && (
        <div className="flex justify-center py-2">
          <button
            type="button"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            className="flex items-center gap-2 rounded-full border border-black-50 px-5 py-2 text-body-sm font-medium text-grey hover:bg-bg-page disabled:opacity-60"
          >
            {isFetchingNextPage ? (
              <>
                <GradientSpinner size={14} />
                <span>Loading more...</span>
              </>
            ) : (
              "Load more"
            )}
          </button>
        </div>
      )}
    </div>
  );
}
